import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import axios from 'axios';

const API = `${API_BASE_URL}/api`;

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState(null);

  const fetchSubscriptionStatus = async (workspaceId) => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/subscriptions/status?workspace_id=${workspaceId}`,
        { withCredentials: true }
      );
      
      setSubscriptionStatus(response.data);
      setCurrentWorkspaceId(workspaceId);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      // Set default to allow access if API fails
      setSubscriptionStatus({
        status: 'trial',
        can_edit: true,
        trial_days_left: 7,
        message: 'Essai gratuit'
      });
    } finally {
      setLoading(false);
    }
  };

  const createCheckoutSession = async (workspaceId) => {
    try {
      const response = await axios.post(
        `${API}/subscriptions/create-checkout`,
        { workspace_id: workspaceId },
        { 
          params: { workspace_id: workspaceId },
          withCredentials: true 
        }
      );
      
      return response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      throw error;
    }
  };

  const canEdit = () => {
    if (!subscriptionStatus) return true; // Allow by default
    return subscriptionStatus.can_edit;
  };

  const isTrialActive = () => {
    return subscriptionStatus?.status === 'trial' && subscriptionStatus?.can_edit;
  };

  const isExpired = () => {
    return subscriptionStatus?.status === 'expired' || 
           (subscriptionStatus?.status === 'trial' && !subscriptionStatus?.can_edit);
  };

  const isActive = () => {
    return subscriptionStatus?.status === 'active' || 
           subscriptionStatus?.status === 'manually_granted';
  };

  const getTrialDaysLeft = () => {
    return subscriptionStatus?.trial_days_left || 0;
  };

  const refreshStatus = async () => {
    if (currentWorkspaceId) {
      await fetchSubscriptionStatus(currentWorkspaceId);
    }
  };

  const value = {
    subscriptionStatus,
    loading,
    fetchSubscriptionStatus,
    createCheckoutSession,
    canEdit,
    isTrialActive,
    isExpired,
    isActive,
    getTrialDaysLeft,
    refreshStatus
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
